import type { ZodRawShape, ZodType } from "zod";
import { z } from "zod";

/**
 * Domain-agnostic Zod schemas for the predicate DSL shared by the tag syncer and the driver query engine.
 *
 * The scalar operators (`eq`, `inArray`, `regex`, `isEmpty`, the time and number comparisons, `withinRadius`,
 * `isTrue`, `isFalse`) and the boolean combinators
 * (`and`, `or`, `not`) are defined once here. Each domain composes these: the tag engine uses the fieldless
 * {@link scalarPredicateSchema} verbatim; the driver query engine augments the value operators with a `field`
 * key via {@link strictLeaf}. Adding an operator here propagates to both engines.
 */

/**
 * Wire spec for a regular-expression operator, kept as strings so a node round-trips through JSON.
 *
 * Validated for compileability at parse time (so an invalid pattern is rejected at save, never silently
 * false at eval) but NOT transformed to a `RegExp`, so callers that serialize the node back to JSON keep
 * the original `{ pattern, flags }` shape.
 */
export const regexSpecSchema = z
  .object({
    pattern: z.string(),
    flags: z.string().optional(),
  })
  .strict()
  .transform((spec, ctx) => {
    try {
      // Compile-check only; the compiled RegExp is intentionally discarded so the node stays serializable.
      // TODO - SVS-2410: swap native RegExp for RE2 to eliminate the ReDoS attack surface on user-supplied
      // regex patterns (evaluated per driver row by both the tag and driver-query engines).
      new RegExp(spec.pattern, spec.flags);
    } catch (e) {
      ctx.addIssue({
        code: "custom",
        message: `Invalid regular expression "${spec.pattern}": ${e instanceof Error ? e.message : "unknown error"}`,
      });
      return z.NEVER;
    }
    return spec;
  });

/** Raw shape for the `eq` (exact string equality) operator. */
export const eqShape = { eq: z.string() } satisfies ZodRawShape;
/** Raw shape for the `inArray` (exact set membership) operator. */
export const inArrayShape = { inArray: z.array(z.string()) } satisfies ZodRawShape;
/** Raw shape for the `regex` operator. */
export const regexShape = { regex: regexSpecSchema } satisfies ZodRawShape;
/** Raw shape for the `isEmpty` (empty or whitespace-only) operator. */
export const isEmptyShape = { isEmpty: z.object({}) } satisfies ZodRawShape;
/** Raw shape for the `isTrue` (always-match) constant. */
export const isTrueShape = { isTrue: z.object({}) } satisfies ZodRawShape;
/** Raw shape for the `isFalse` (never-match) constant. */
export const isFalseShape = { isFalse: z.object({}) } satisfies ZodRawShape;
/** Raw shape for `olderThan`: an ISO timestamp more than N minutes before the evaluation instant. */
export const olderThanShape = {
  olderThan: z.object({ minutes: z.number().nonnegative() }).strict(),
} satisfies ZodRawShape;
/** Raw shape for `newerThan`: an ISO timestamp within the last N minutes of the evaluation instant. */
export const newerThanShape = {
  newerThan: z.object({ minutes: z.number().nonnegative() }).strict(),
} satisfies ZodRawShape;
/** Raw shape for `gt`: the value parses as a number greater than the comparand. */
export const gtShape = { gt: z.number() } satisfies ZodRawShape;
/** Raw shape for `lt`: the value parses as a number less than the comparand. */
export const ltShape = { lt: z.number() } satisfies ZodRawShape;
/**
 * Raw shape for `withinRadius`: the value, a `"latitude,longitude"` pair, lies within `radius` of the center.
 *
 * `label` is display only (the address the center was picked from) so a chip can name the place; evaluation ignores it.
 */
export const withinRadiusShape = {
  withinRadius: z
    .object({
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
      radius: z.number().positive(),
      unit: z.enum(["miles", "km"]),
      label: z.string().optional(),
    })
    .strict(),
} satisfies ZodRawShape;

/**
 * Builds a strict single-operator leaf schema from a raw shape.
 *
 * Strictness rejects any unknown key, so a spec may carry exactly one operator (plus whatever `extra` keys a
 * domain injects, e.g. the driver query engine's `field`). Extra keys are merged into the object.
 *
 * @param shape - the operator shape (e.g. {@link eqShape}).
 * @param extra - additional keys to merge into the leaf (e.g. `{ field: z.string().min(1) }`).
 */
export function strictLeaf<Shape extends ZodRawShape, Extra extends ZodRawShape = Record<string, never>>(
  shape: Shape,
  extra?: Extra,
) {
  return z.object({ ...(extra ?? ({} as Extra)), ...shape }).strict();
}

/**
 * The fieldless scalar predicate union: every value operator, the time and number comparisons, and the constants.
 *
 * Used verbatim by the tag engine (which evaluates a single string) and as the operator vocabulary the
 * driver query engine re-derives with a `field` dimension.
 */
export const scalarPredicateSchema = z.union([
  // Value operators first, then the fieldless constants.
  strictLeaf(eqShape),
  strictLeaf(inArrayShape),
  strictLeaf(regexShape),
  strictLeaf(isEmptyShape),
  strictLeaf(olderThanShape),
  strictLeaf(newerThanShape),
  strictLeaf(gtShape),
  strictLeaf(ltShape),
  strictLeaf(withinRadiusShape),
  strictLeaf(isTrueShape),
  strictLeaf(isFalseShape),
]);

/**
 * Builds the recursive combinator schemas (`and`/`or`/`not` and the `any` leaf-or-combinator union) over a leaf.
 *
 * Empty `and` (vacuously true) and empty `or` (vacuously false) are both accepted; constants are canonically
 * expressed via `isTrue`/`isFalse` leaves rather than empty combinators.
 *
 * With an `id`, the `any` union is named for an OpenAPI document before the combinators close over it, so every
 * `and`/`or`/`not` arm references the named component and the generator documents the recursion instead of expanding
 * it forever. The name must be applied here: naming the returned union afterwards leaves the arms pointing at the
 * unnamed one, and the generator overflows the stack.
 *
 * @param leaf - the scalar leaf union this DSL combines (e.g. {@link scalarPredicateSchema}).
 * @param id - the component name the `any` union carries on the wire, when it has one.
 */
export function combinatorSchemas(
  leaf: ZodType,
  id?: string,
): {
  any: ZodType<unknown>;
  and: ZodType;
  or: ZodType;
  not: ZodType;
} {
  const union: ZodType<unknown> = z.lazy(() => z.union([leaf, and, or, not]));
  const any = id === undefined ? union : union.meta({ id });
  const and = z.object({ and: z.array(any) }).strict();
  const or = z.object({ or: z.array(any) }).strict();
  const not = z.object({ not: any }).strict();
  return { any, and, or, not };
}

/**
 * The fieldless predicate DSL: the shared scalar operators combined with `and`/`or`/`not`.
 *
 * Used by engines that evaluate a single string (e.g. the tag engine). Empty `and`/`or` are accepted
 * (vacuously true/false).
 */
export const anyPredicateSchema = combinatorSchemas(scalarPredicateSchema).any;

/**
 * TypeScript input type for a scalar predicate.
 */
export type ScalarPredicateInput = z.input<typeof scalarPredicateSchema>;

/**
 * TypeScript input type covering the full recursive fieldless predicate DSL — scalar or compound.
 */
export type AnyPredicateInput = PredicateNode<ScalarPredicateInput>;

/**
 * TypeScript type for the full recursive predicate DSL over a given leaf type — a leaf or a combinator.
 */
export type PredicateNode<Leaf> =
  | Leaf
  | { and: PredicateNode<Leaf>[] }
  | { or: PredicateNode<Leaf>[] }
  | { not: PredicateNode<Leaf> };
